/**
 * The workspace permission model, as the front end needs it.
 *
 * A copy of `api/src/shared/lib/permissions.ts` — the apps share no code — so
 * the two must agree on the area keys, the levels and their order. The API is
 * what enforces any of this; here it only decides what to show.
 */

export const LEVELS = ["none", "view", "edit", "full"] as const;
export type Level = (typeof LEVELS)[number];

const RANK: Record<Level, number> = { none: 0, view: 1, edit: 2, full: 3 };

export function atLeast(held: Level | undefined, required: Level): boolean {
  return RANK[held ?? "none"] >= RANK[required];
}

export const AREAS = [
  "employees",
  "documents",
  "requests",
  "leave",
  "onboarding",
  "attendance",
  "incidents",
  "assets",
  "broadcasts",
  "policies",
  "library",
  "alerts",
  "credentials",
  "audit",
  "settings",
  "team",
] as const;
export type Area = (typeof AREAS)[number];

export type Permissions = Record<Area, Level>;

export const LEVEL_LABELS: Record<Level, string> = {
  none: "No access",
  view: "View",
  edit: "Edit",
  full: "Full",
};

/** What each level means in a given area, for the role editor's hints. */
export const LEVEL_HINTS: Record<Level, string> = {
  none: "Hidden from the sidebar; can't open it.",
  view: "Can see everything here, can't change anything.",
  edit: "Can add and change, but not delete.",
  full: "Can also delete, approve and change this area's settings.",
};

/**
 * Which area each workspace page belongs to, by path prefix. Longest match
 * wins; a page not listed here is open to every member.
 */
const PAGE_AREAS: ReadonlyArray<readonly [string, Area]> = [
  ["/employees", "employees"],
  ["/generate", "documents"],
  ["/documents", "documents"],
  ["/document", "documents"],
  ["/attendance", "attendance"],
  ["/requests", "requests"],
  ["/leave", "leave"],
  ["/onboarding", "onboarding"],
  ["/incidents", "incidents"],
  ["/assets", "assets"],
  ["/broadcasts", "broadcasts"],
  ["/policies", "policies"],
  ["/library", "library"],
  ["/alerts", "alerts"],
  ["/credentials", "credentials"],
  ["/settings/credentials", "credentials"],
  ["/audit", "audit"],
  ["/settings/company", "settings"],
  ["/settings/naming", "settings"],
  ["/settings/letterheads", "settings"],
  ["/settings/attendance", "attendance"],
  ["/settings/team", "team"],
];

export function areaForPath(pathname: string): Area | null {
  let match: readonly [string, Area] | undefined;
  for (const entry of PAGE_AREAS) {
    const [prefix] = entry;
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      if (!match || prefix.length > match[0].length) match = entry;
    }
  }
  return match?.[1] ?? null;
}

/** Pages that only create or change things, so opening one needs Edit. */
const EDIT_PAGES: readonly RegExp[] = [
  /^\/employees\/new$/,
  /^\/employees\/[^/]+\/edit$/,
  /^\/generate(\/.*)?$/,
  // Letterhead management is company settings, and the API lists them only
  // to someone who can change them.
  /^\/settings\/letterheads$/,
];

/** What opening a page needs: its area and level, or `null` for an open page. */
export function pageRequirement(pathname: string): { area: Area; level: Level } | null {
  const area = areaForPath(pathname);
  if (!area) return null;
  return { area, level: EDIT_PAGES.some((re) => re.test(pathname)) ? "edit" : "view" };
}

/** Where to send someone whose role can't open the page they asked for: the
 *  first area in sidebar order they can see, or their account settings. */
export function homeFor(permissions: Permissions): string {
  const order: ReadonlyArray<readonly [Area, string]> = [
    ["employees", "/employees"],
    ["documents", "/documents"],
    ["attendance", "/attendance"],
    ["requests", "/requests"],
    ["leave", "/leave"],
    ["onboarding", "/onboarding"],
    ["incidents", "/incidents"],
    ["assets", "/assets"],
    ["broadcasts", "/broadcasts"],
    ["policies", "/policies"],
    ["library", "/library"],
    ["alerts", "/alerts"],
    ["credentials", "/credentials"],
    ["audit", "/audit"],
  ];
  return order.find(([area]) => atLeast(permissions[area], "view"))?.[1] ?? "/settings/account";
}

/** Whether the dashboard has anything to show this role. It is built from
 *  employees, documents, alerts and requests. */
export function hasDashboard(permissions: Permissions): boolean {
  return (["employees", "documents", "alerts", "requests"] as const).some((area) =>
    atLeast(permissions[area], "view"),
  );
}
